import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { api, BRAND_LABELS, fmtRM } from "@/lib/api";
import { waLink, WhatsAppIcon } from "@/lib/whatsapp";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import ModelCard from "@/components/ModelCard";
import SpecModal from "@/components/SpecModal";

const HERO_IMG = "/cars/hero-typer.jpg";

const BRANDS = [
  { slug: "honda", num: "01", tag: "Flagship", name: "Honda", desc: "Japanese engineering trust. Our founding brand.", img: "/cars/showcase-honda.jpg" },
  { slug: "proton", num: "02", tag: "National Brand", name: "Proton", desc: "Malaysian innovation at direct prices. From the Saga to the X90.", img: "/cars/showcase-proton.jpg" },
  { slug: "jaecoo", num: "03", tag: "New · 2026", name: "Jaecoo & Omoda", desc: "Next-gen luxury SUVs and plug-in hybrids. New to Malaysia.", img: "/cars/showcase-jaecoo.jpg" },
];

const MANIFESTO = [
  { num: "01", title: "No Agent, Ever", body: "No broker between you and your car means no broker fee hiding in your price." },
  { num: "02", title: "Direct Pricing", body: "Straight from dealer to customer. What we quote is what you pay — the best price, guaranteed." },
];

const HeroLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1 -mb-1">
    <motion.span
      className="block whitespace-nowrap"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [offers, setOffers] = useState([]);
  const [launchCar, setLaunchCar] = useState(null);
  const [launchCarNA, setLaunchCarNA] = useState(null);
  const [active, setActive] = useState(null);
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 900], [0, 130]);
  const markY = useTransform(scrollY, [0, 900], [0, -70]);

  useEffect(() => {
    document.title = "AutoWorld — Honda · Proton · Jaecoo & Omoda";
    api.models({ featured: true }).then((d) => setFeatured(d.slice(0, 6))).catch(() => {});
    api.models({ brand: "honda" }).then((d) => setLaunchCar(d.find((m) => m.name === "City") || null)).catch(() => {});
    api.models({ brand: "proton" }).then((d) => setLaunchCarNA(d.find((m) => m.name === "S70 NA") || null)).catch(() => {});
    api.offers().then((d) => setOffers(d.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden pt-16 md:pt-20" data-testid="hero-section">
        <motion.img
          src="/aw-logo.png"
          alt=""
          aria-hidden="true"
          style={{ y: markY }}
          className="pointer-events-none absolute -right-10 top-24 w-[55vw] select-none opacity-[0.05] md:w-[36rem]"
        />
        <div className="relative mx-auto grid max-w-[1400px] gap-10 px-5 pb-16 pt-10 md:grid-cols-12 md:px-10 md:pb-24 md:pt-16">
          <div className="flex flex-col justify-center max-md:items-center max-md:text-center md:col-span-7">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="font-mono text-[10px] uppercase tracking-[0.35em] text-neutral-500"
              data-testid="hero-eyebrow"
            >
              AutoWorld · Honda · Proton · Jaecoo &amp; Omoda
            </motion.div>
            <h1 className="mt-6 font-display font-extrabold uppercase leading-[0.95] tracking-tighter" style={{ fontSize: "clamp(1.85rem, 4.6vw, 4.6rem)" }} data-testid="hero-headline">
              <HeroLine delay={0.35}>No Agent.</HeroLine>
              <HeroLine delay={0.5}>No Markup.</HeroLine>
              <HeroLine delay={0.65}>
                <span className="text-stroke">Best Price.</span>
              </HeroLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0, duration: 0.8 }}
              className="mt-8 max-w-md text-base leading-relaxed text-neutral-600 max-md:mx-auto md:text-lg"
              data-testid="hero-subcopy"
            >
              Honda, Proton, and Jaecoo &amp; Omoda — straight from dealer to customer,
              so the best price is guaranteed.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.15, duration: 0.8 }}
              className="mt-8 flex flex-wrap gap-3 max-md:justify-center"
            >
              <Link
                to="/models"
                data-testid="hero-browse-models"
                className="group flex items-center gap-2 bg-black px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-colors hover:bg-neutral-800"
              >
                Browse Models <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href={waLink()}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-whatsapp"
                className="flex items-center gap-2 border border-black px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] transition-colors hover:bg-black hover:text-white"
              >
                <WhatsAppIcon className="h-4 w-4" /> WhatsApp Us
              </a>
            </motion.div>
          </div>

          <div className="relative md:col-span-5">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ delay: 0.55, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="relative aspect-[3/4] overflow-hidden border border-black/10"
              data-testid="hero-image-frame"
            >
              <motion.img
                src={HERO_IMG}
                alt="Honda Civic Type R at AutoWorld"
                style={{ y: imgY, scale: 1.15 }}
                className="h-full w-full object-cover"
              />
              <div className="absolute bottom-0 left-0 bg-black px-4 py-2.5 font-mono text-[9px] uppercase tracking-[0.3em] text-white">
                Direct · Guaranteed
              </div>
            </motion.div>
          </div>
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
          className="relative mx-auto flex max-w-[1400px] items-center gap-3 px-5 pb-8 md:px-10"
        >
          <ArrowDown className="h-4 w-4 animate-bounce" />
          <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-neutral-400">Scroll</span>
        </motion.div>
      </section>

      <Marquee items={["No Agent", "No Markup", "Best Price Guaranteed", "Honda", "Proton", "Jaecoo & Omoda"]} />

      {/* STATS */}
      <section className="mx-auto max-w-[1400px] px-5 md:px-10" data-testid="stats-section">
        <div className="grid grid-cols-2 border-x border-b border-black/10 md:grid-cols-4">
          {[
            ["No", "Agent · No markup"],
            ["03", "Brands — Honda · Proton · Jaecoo & Omoda"],
            ["100%", "Transparent pricing"],
            ["01", "Promise — best price"],
          ].map(([n, l], i) => (
            <Reveal key={l} delay={i * 0.08} className={`overflow-hidden border-black/10 p-6 md:p-10 ${i % 2 === 0 ? "border-r" : ""} ${i < 2 ? "max-md:border-b" : ""} md:border-r md:last:border-r-0`}>
              <div className="whitespace-nowrap font-display text-4xl font-extrabold leading-none tracking-tighter xl:text-6xl">{n}</div>
              <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500">{l}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-32" data-testid="manifesto-section">
        <Reveal>
          <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-neutral-500">The AutoWorld Manifesto</div>
          <h2 className="mt-4 max-w-3xl font-display text-4xl font-extrabold uppercase tracking-tighter md:text-6xl">
            Why buy direct
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-px border border-black/10 bg-black/10 md:grid-cols-2">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.num} delay={i * 0.08} className="group bg-white p-8 transition-colors hover:bg-black md:p-12">
              <div className="font-gothic text-6xl text-black/10 transition-colors group-hover:text-white/15 md:text-7xl">{m.num}</div>
              <h3 className="mt-4 font-display text-2xl font-bold uppercase tracking-tight transition-colors group-hover:text-white md:text-3xl">
                {m.title}
              </h3>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-neutral-600 transition-colors group-hover:text-neutral-300 md:text-base">
                {m.body}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BRANDS */}
      <section className="border-t border-black/10" data-testid="brands-section">
        <div className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-32">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-neutral-500">Three Brands</div>
              <h2 className="mt-4 font-display text-4xl font-extrabold uppercase tracking-tighter md:text-6xl">
                Our Brands
              </h2>
            </div>
            <Link to="/models" data-testid="brands-view-all" className="group flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] hover:opacity-60">
              View all models <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>

          <div className="mt-14 space-y-16 md:space-y-24">
            {BRANDS.map((b, i) => (
              <Reveal key={b.slug}>
                <Link
                  to={`/brands/${b.slug}`}
                  data-testid={`brand-showcase-${b.slug}`}
                  className={`group grid items-center gap-8 md:grid-cols-2 ${i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""}`}
                >
                  <div className="relative overflow-hidden border border-black/10">
                    <img src={b.img} alt={b.name} loading="lazy" className="img-bw aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <span className="absolute left-0 top-0 bg-black px-3 py-2 font-mono text-[9px] uppercase tracking-[0.3em] text-white">{b.tag}</span>
                  </div>
                  <div>
                    <div className="font-gothic text-7xl leading-none text-black/10 md:text-8xl">{b.num}</div>
                    <h3 className="mt-2 font-display text-3xl font-extrabold uppercase tracking-tighter md:text-5xl">{b.name}</h3>
                    <p className="mt-4 max-w-md text-neutral-600">{b.desc}</p>
                    <span className="mt-6 inline-flex items-center gap-2 border-b-2 border-black pb-1 font-mono text-[11px] uppercase tracking-[0.25em] transition-all group-hover:gap-4">
                      Explore {b.name} <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* NEW LAUNCH SPOTLIGHT */}
      {launchCar && (
        <section className="bg-black text-white" data-testid="launch-section">
          <div className="mx-auto grid max-w-[1400px] items-center gap-10 px-5 py-16 lg:grid-cols-2 md:px-10 md:py-28">
            <Reveal>
              <div className="inline-block bg-red-600 px-3 py-2 font-mono text-[9px] font-bold uppercase tracking-[0.3em] text-black" data-testid="launch-badge">
                Just Launched · New Facelift
              </div>
              <h2 className="mt-5 font-display font-extrabold uppercase tracking-tighter" style={{ fontSize: "clamp(2.2rem, 4.6vw, 4.2rem)", lineHeight: 1.02 }}>
                The New Honda City
              </h2>
              <p className="mt-4 max-w-md text-neutral-400">
                {launchCar.tagline}. Fresh face, same legend — now in showrooms.
              </p>
              <div className="mt-6 flex items-center gap-4">
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500">From</div>
                  <div className="font-display text-3xl font-extrabold" data-testid="launch-price">{fmtRM(launchCar.price_from)}</div>
                </div>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  data-testid="launch-details"
                  onClick={() => setActive(launchCar)}
                  className="bg-white px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-black transition-colors hover:bg-neutral-200"
                >
                  See Variants
                </button>
                <a
                  href={waLink("Hi AutoWorld! I want the best price for the new Honda City facelift!")}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="launch-whatsapp"
                  className="flex items-center gap-2 border border-white px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] transition-colors hover:bg-white hover:text-black"
                >
                  <WhatsAppIcon className="h-4 w-4" /> WhatsApp Us
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="overflow-hidden bg-white" data-testid="launch-image">
                <img src="/cars/launch-city.jpg" alt="The new Honda City facelift" loading="lazy" className="img-bw aspect-[16/10] w-full object-cover" />
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* NEW LAUNCH SPOTLIGHT — PROTON S70 NA */}
      {launchCarNA && (
        <section className="border-t border-black/10 bg-white text-black" data-testid="launch-na-section">
          <div className="mx-auto grid max-w-[1400px] items-center gap-10 px-5 py-16 lg:grid-cols-2 md:px-10 md:py-28">
            <Reveal className="lg:order-2">
              <div className="inline-block bg-red-600 px-3 py-2 font-mono text-[9px] font-bold uppercase tracking-[0.3em] text-black" data-testid="launch-na-badge">
                Just Launched · New Addition
              </div>
              <h2 className="mt-5 font-display font-extrabold uppercase tracking-tighter" style={{ fontSize: "clamp(2.2rem, 4.6vw, 4.2rem)", lineHeight: 1.02 }}>
                The New Proton S70 NA
              </h2>
              <p className="mt-4 max-w-md text-neutral-500">
                {launchCarNA.tagline} — now in showrooms.
              </p>
              <div className="mt-6 flex items-center gap-4">
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-400">From</div>
                  <div className="font-display text-3xl font-extrabold" data-testid="launch-na-price">{fmtRM(launchCarNA.price_from)}</div>
                </div>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  data-testid="launch-na-details"
                  onClick={() => setActive(launchCarNA)}
                  className="bg-black px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-colors hover:bg-neutral-800"
                >
                  See Variants
                </button>
                <a
                  href={waLink("Hi AutoWorld! I want the best price for the new Proton S70 NA!")}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="launch-na-whatsapp"
                  className="flex items-center gap-2 border border-black px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] transition-colors hover:bg-black hover:text-white"
                >
                  <WhatsAppIcon className="h-4 w-4" /> WhatsApp Us
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.15} className="lg:order-1">
              <div className="overflow-hidden bg-neutral-100" data-testid="launch-na-image">
                <img src={launchCarNA.image} alt="The new Proton S70 NA" loading="lazy" className="img-bw aspect-[16/10] w-full object-cover" />
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* FEATURED MODELS */}
      <section className="border-t border-black/10 bg-neutral-50" data-testid="featured-section">
        <div className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-32">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-neutral-500">In the Spotlight</div>
              <h2 className="mt-4 font-display text-4xl font-extrabold uppercase tracking-tighter md:text-6xl">Featured Models</h2>
            </div>
            <Link to="/models" data-testid="featured-view-all" className="group flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] hover:opacity-60">
              Full lineup <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((m, i) => (
              <ModelCard key={m.id} model={m} index={i} onView={setActive} />
            ))}
          </div>
        </div>
      </section>

      {/* OFFERS TEASER */}
      {offers.length > 0 && (
        <section className="bg-black text-white" data-testid="offers-teaser">
          <div className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-28">
            <Reveal className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-neutral-400">July 2026</div>
                <h2 className="mt-4 font-display text-4xl font-extrabold uppercase tracking-tighter md:text-6xl">This Month's Offers</h2>
              </div>
              <Link to="/offers" data-testid="offers-teaser-link" className="group flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-300 hover:text-white">
                All offers <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
            <div className="mt-12 grid gap-px border border-white/15 bg-white/15 md:grid-cols-3">
              {offers.map((o, i) => (
                <Reveal key={o.id} delay={i * 0.08} className="bg-black p-8">
                  <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-neutral-500">{BRAND_LABELS[o.brand] || "All Brands"}</div>
                  <div className="mt-3 font-display text-3xl font-extrabold uppercase tracking-tight">{o.highlight}</div>
                  <p className="mt-3 text-sm text-neutral-400">{o.title}</p>
                  <a
                    href={waLink(`Hi AutoWorld! I want to claim: ${o.title} (${o.highlight}).`)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`offer-claim-${o.id}`}
                    className="mt-6 inline-flex items-center gap-2 border-b border-white pb-1 font-mono text-[10px] uppercase tracking-[0.25em] hover:opacity-70"
                  >
                    <WhatsAppIcon className="h-3.5 w-3.5" /> Claim on WhatsApp
                  </a>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* BULK + CALC SPLIT */}
      <section className="grid border-b border-black/10 lg:grid-cols-2" data-testid="split-cta">
        <Reveal className="flex flex-col justify-center border-b border-black/10 p-10 lg:border-b-0 lg:border-r lg:p-20">
          <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-neutral-500">Fleet · Corporate · Group</div>
          <h2 className="mt-4 font-display text-3xl font-extrabold uppercase tracking-tighter lg:text-5xl">Bulk Purchase Program</h2>
          <p className="mt-4 max-w-md text-neutral-600">
            Buying 2 or more units? Unlock volume discounts with nationwide door-to-door delivery.
          </p>
          <Link to="/bulk" data-testid="split-bulk-link" className="group mt-8 inline-flex w-fit items-center gap-2 bg-black px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-white hover:bg-neutral-800">
            Fleet deals <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
        <Reveal className="flex flex-col justify-center p-10 lg:p-20" delay={0.1}>
          <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-neutral-500">First Car? Do the Math</div>
          <h2 className="mt-4 font-display text-3xl font-extrabold uppercase tracking-tighter lg:text-5xl">Loan Calculator</h2>
          <p className="mt-4 max-w-md text-neutral-600">
            See your real monthly installment in seconds — then apply straight through WhatsApp.
          </p>
          <Link to="/calculator" data-testid="split-calc-link" className="group mt-8 inline-flex w-fit items-center gap-2 border border-black px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] hover:bg-black hover:text-white">
            Calculate now <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden bg-black py-24 text-white md:py-36" data-testid="final-cta">
        <img
          src="/aw-logo.png"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-1/2 w-[80vw] max-w-[42rem] -translate-x-1/2 -translate-y-1/2 select-none opacity-[0.05] invert"
        />
        <div className="relative mx-auto max-w-[1400px] px-5 text-center md:px-10">
          <Reveal>
            <h2 className="font-display font-extrabold uppercase tracking-tighter" style={{ fontSize: "clamp(2.1rem, 8.5vw, 6rem)" }}>
              Ready to deal<br /><span className="text-stroke-white">direct?</span>
            </h2>
            <p className="mx-auto mt-6 max-w-md text-neutral-400">
              One message. Best price, straight from the source. No forms, no waiting rooms.
            </p>
            <a
              href={waLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="final-cta-whatsapp"
              className="mt-10 inline-flex items-center gap-3 bg-white px-10 py-5 font-mono text-xs uppercase tracking-[0.3em] text-black transition-transform hover:scale-105"
            >
              <WhatsAppIcon className="h-5 w-5" /> Chat on WhatsApp
            </a>
          </Reveal>
        </div>
      </section>

      <SpecModal model={active} onClose={() => setActive(null)} />
    </div>
  );
}
